import { useMemo, useState, type ReactNode } from "react";
import moment from "moment/moment";
import { IconDownload, IconPlus } from "@tabler/icons-react";
import Button from "../UI/Button";
import { Table } from "../UI/Table";
import Text from "../UI/Text";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection from "../UI/FilterBar/FilterSection";
import StatusCell from "../Pilotage/StatusCell";
import StatusFilterLabel from "../Pilotage/StatusFilterLabel";
import PilotageHeader from "../Pilotage/PilotageHeader";
import FilterableColumnHeader from "../Pilotage/FilterableColumnHeader";
// @ts-expect-error pure js
import useTenantDocumentSynthese from "../../hooks/use-tenant-document-synthese";
// @ts-expect-error pure js
import CheckDocuments from "../UI/CheckDocuments";
// @ts-expect-error pure js
import { FileTag } from "../../utils/constants/documents";
import {
  getTenantDocumentState,
  type TenantDocumentState,
  type TenantDocumentStatusCode,
} from "../../utils/documentStatus";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
import TenantPropertyCell from "./TenantPropertyCell";
import useTableSort from "../../hooks/use-table-sort";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
import { TenantLeaseType } from "../../utils/constants/tenants";
import { downloadCsv } from "../../utils/csv";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { DocumentFile } from "../../models/document";
import styles from "./MassSynthese.module.css";

type CellStatus = TenantDocumentStatusCode | "visale" | "nonRequis";

interface CellState {
  status: CellStatus;
  validUntil?: string;
}

const STATUS_FILTER_OPTIONS = [
  {
    value: "aValider",
    label: <StatusFilterLabel text="À valider" color="info" />,
  },
  {
    value: "absent",
    label: (
      <StatusFilterLabel text="À ajouter" color="neutral" icon={IconPlus} />
    ),
  },
  {
    value: "expire",
    label: <StatusFilterLabel text="Expiré" color="danger" />,
  },
  {
    value: "bientotExpire",
    label: <StatusFilterLabel text="Expire bientôt" color="warning" />,
  },
  {
    value: "valide",
    label: <StatusFilterLabel text="Validé / Signé" color="success" />,
  },
  {
    value: "visale",
    label: <StatusFilterLabel text="Visale" color="success" />,
  },
  { value: "nonRequis", label: <StatusFilterLabel text="Non requis" /> },
];

const COLUMN_CONFIG: { tag: string; label: string }[] = [
  { tag: FileTag.BAIL, label: "Bail" },
  { tag: FileTag.EDL, label: "État des lieux" },
  { tag: FileTag.ASSURANCE_HABITATION, label: "Assurance" },
  { tag: FileTag.ENTRETIEN_CHAUFFAGE, label: "Entretien chauffage" },
  { tag: FileTag.CAUTION, label: "Cautionnement" },
];

const EXPORT_LABELS: Record<CellStatus, string> = {
  absent: "À ajouter",
  expire: "Expiré",
  aValider: "À valider",
  bientotExpire: "Expire bientôt",
  valide: "Validé / Signé",
  visale: "Visale",
  nonRequis: "Non requis",
};

const activeMainProfiles = (tenant: Tenant) =>
  tenant.profiles?.filter(
    (profile) =>
      profile.garantor_of == null &&
      profile.status === ProfileStatus.Actif.status,
  ) ?? [];

// Une garantie Visale remplace l'acte de cautionnement : il n'y a pas de
// document à fournir.
const hasVisaleGuarantee = (tenant: Tenant, files: DocumentFile[] = []) =>
  files.every((file) => file.tag !== FileTag.CAUTION) &&
  activeMainProfiles(tenant).some(
    (profile) => profile.caution_type === "Visale",
  );

// Une assurance loyers impayés ne donne pas lieu à un acte de cautionnement.
const hasOnlyGliGuarantee = (tenant: Tenant) => {
  const profiles = activeMainProfiles(tenant);
  return (
    profiles.length > 0 &&
    profiles.every((profile) => profile.caution_type === "GLI")
  );
};

function getCellState(
  tenant: Tenant,
  files: DocumentFile[] | undefined,
  tag: string,
): CellState {
  if (tag === FileTag.CAUTION) {
    if (hasVisaleGuarantee(tenant, files)) {
      return { status: "visale" };
    }
    if (hasOnlyGliGuarantee(tenant)) {
      return { status: "nonRequis" };
    }
  }
  // Même règle que les automatisations : l'entretien de la chaudière ne
  // concerne que les baux d'habitation.
  if (
    tag === FileTag.ENTRETIEN_CHAUFFAGE &&
    !TenantLeaseType.isHabitation(tenant.lease_type)
  ) {
    return { status: "nonRequis" };
  }
  return getTenantDocumentState(files, tag);
}

function getExportText(state: CellState): string {
  return state.validUntil
    ? `${EXPORT_LABELS[state.status]} (jusqu'au ${moment(
        state.validUntil,
      ).format("DD/MM/YYYY")})`
    : EXPORT_LABELS[state.status];
}

interface MassSyntheseProps {
  tenants: Tenant[];
  /** Filtre « Biens et dossiers » de la page, partagé avec les autres onglets. */
  bienFilter?: ReactNode;
  bienFilterCount: number;
  onClearBienFilter: () => void;
}

/** Onglet Suivi documents du pilotage : l'état des documents de chaque locataire actif. */
export default function MassSynthese({
  tenants,
  bienFilter,
  bienFilterCount,
  onClearBienFilter,
}: MassSyntheseProps) {
  const { tenantDocumentSynthese, mutate } = useTenantDocumentSynthese() as {
    tenantDocumentSynthese: Record<number, DocumentFile[] | undefined>;
    mutate: () => Promise<void>;
  };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property | undefined>;
  };
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    {},
  );
  const { sort, toggleSort, directionFor } =
    useTableSort<"nom">("massSynthese_sort");

  const hasColumnFilter = Object.values(columnFilters).some(
    (values) => values.length > 0,
  );

  const rows = useMemo(() => {
    const searchLower = search.toLowerCase();
    const result = tenants
      .map((tenant) => ({
        tenant,
        property: propertiesById[tenant.id_bien],
        files: tenantDocumentSynthese[tenant.id],
        states: Object.fromEntries(
          COLUMN_CONFIG.map(({ tag }) => [
            tag,
            getCellState(tenant, tenantDocumentSynthese[tenant.id], tag),
          ]),
        ) as Record<string, CellState>,
      }))
      .filter(
        ({ tenant, property }) =>
          !searchLower ||
          tenant.nom?.toLowerCase().includes(searchLower) ||
          property?.nom?.toLowerCase().includes(searchLower),
      )
      // Filtre global : un document quelconque a un des statuts choisis.
      // Filtres de colonne : chaque colonne filtrée a un de ses statuts.
      .filter(
        ({ states }) =>
          (statusFilter.length === 0 ||
            COLUMN_CONFIG.some(({ tag }) =>
              statusFilter.includes(states[tag].status),
            )) &&
          COLUMN_CONFIG.every(({ tag }) => {
            const values = columnFilters[tag] ?? [];
            return values.length === 0 || values.includes(states[tag].status);
          }),
      );
    if (sort) {
      result.sort(
        (a, b) =>
          (sort.direction === "asc" ? 1 : -1) *
          (a.tenant.nom ?? "").localeCompare(b.tenant.nom ?? "", "fr", {
            sensitivity: "base",
          }),
      );
    }
    return result;
  }, [
    tenants,
    propertiesById,
    tenantDocumentSynthese,
    search,
    statusFilter,
    columnFilters,
    sort,
  ]);

  const exportCsv = () =>
    downloadCsv(`documents-locataires-${moment().format("YYYY-MM-DD")}.csv`, [
      ["Locataire", "Bien", ...COLUMN_CONFIG.map(({ label }) => label)],
      ...rows.map(({ tenant, property, states }) => [
        tenant.nom,
        property?.nom ?? "",
        ...COLUMN_CONFIG.map(({ tag }) => getExportText(states[tag])),
      ]),
    ]);

  return (
    <>
      <PilotageHeader
        title="Documents locataires"
        actions={
          <Button
            variant="secondary"
            icon={IconDownload}
            disabled={rows.length === 0}
            onClick={exportCsv}
          >
            Exporter
          </Button>
        }
      />
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Rechercher un locataire, un bien"
        activeFiltersCount={
          (statusFilter.length > 0 ? 1 : 0) +
          (hasColumnFilter ? 1 : 0) +
          bienFilterCount
        }
        onClearFilters={() => {
          setStatusFilter([]);
          setColumnFilters({});
          onClearBienFilter();
        }}
      >
        <FilterSection
          title="Statut"
          options={STATUS_FILTER_OPTIONS}
          values={statusFilter}
          onChange={setStatusFilter}
          defaultOpen
        />
        {bienFilter}
      </FilterBar>
      <Table variant="card" stickyFirstColumn>
        <Table.Header>
          <Table.Column>
            <div className={styles.firstHeader}>
              <Table.SortButton
                direction={directionFor("nom")}
                onSort={() => toggleSort("nom")}
              >
                Locataire
              </Table.SortButton>
              <Text as="span" color="muted">
                {rows.length} locataire{rows.length > 1 ? "s" : ""}
              </Text>
            </div>
          </Table.Column>
          {COLUMN_CONFIG.map(({ tag, label }) => (
            <Table.Column key={tag}>
              <FilterableColumnHeader
                label={label}
                options={STATUS_FILTER_OPTIONS}
                values={columnFilters[tag] ?? []}
                onChange={(values) =>
                  setColumnFilters((prev) => ({ ...prev, [tag]: values }))
                }
              />
            </Table.Column>
          ))}
        </Table.Header>
        <Table.Body>
          {rows.map(({ tenant, property, files, states }) => (
            <Table.Row key={tenant.id}>
              <Table.Cell>
                <TenantPropertyCell tenant={tenant} property={property} />
              </Table.Cell>
              {COLUMN_CONFIG.map(({ tag }) => (
                <Table.Cell key={tag}>
                  {states[tag].status === "visale" ? (
                    <img
                      src="/logo-visale.png"
                      alt="Garantie Visale"
                      height={24}
                    />
                  ) : states[tag].status === "nonRequis" ? (
                    <StatusCell variant="muted" text="Non requis" />
                  ) : (
                    <CheckDocuments
                      files={files}
                      tag={tag}
                      tenant={tenant}
                      state={states[tag] as TenantDocumentState}
                      onDocumentSubmitted={mutate}
                    />
                  )}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
}
